import React from 'react';
import { get, find } from 'lodash';
import { Route } from 'react-router-dom';
import WindowResize from '../common/WindowResize';
import AttributesLarge from './attributesLarge/AttributesLarge';
import Profile from './carousel/Profile';
import BreakpointMatch from '../common/mediaQuery/BreakpointMatch';
import Carousel from './carousel/Carousel';
import { LayoutContext } from './LayoutManager';
import { translate } from '../common/translate/translations';

const NATURAL_IMAGE_WIDTH = 800;
const NATURAL_IMAGE_HEIGHT = 450;
const imageRatio = NATURAL_IMAGE_WIDTH / NATURAL_IMAGE_HEIGHT;
const MAX_CROP_AMOUNT = 0.8;

const getParentWidth = ref =>
  ref.current ? ref.current.parentElement.clientWidth : 0;

const computeDisclaimerKey = (config, isFamilyFirstUS) => {
  const { eligibleForInvoicePrice, modelMatrix } = config;
  const { styleInformation, selectedStyleId } = modelMatrix;
  const selectedStyle = find(styleInformation, { id: selectedStyleId });
  const cgiDisclaimer = get(selectedStyle, 'vehicleInfo.cgiDisclaimer');

  if (isFamilyFirstUS && !eligibleForInvoicePrice) {
    return [translate('ineligible.model'), cgiDisclaimer].filter(x => x);
  }

  return [cgiDisclaimer].filter(x => x);
};

class CgiSection extends React.Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  render() {
    const {
      config,
      isFamilyFirstUS,
      attributes,
      images,
      isConvertibleTopDown,
      isInteriorPrimary,
      interiorIndex,
      exteriorIndex,
      onToggleConvertibleTop,
      onToggleInterior,
      onChangeInteriorIndex,
      onChangeExteriorIndex
    } = this.props;

    const ref = this.ref;
    const isConvertible = get(config.bodyType, 'showConvertible', false);
    const isInteriorEnabled = images.interior.length > 0;
    const cgiDisclaimers = computeDisclaimerKey(config, isFamilyFirstUS);

    return (
      <LayoutContext.Consumer>
        {({ height: yOffset }) => (
          <WindowResize>
            {({ height: windowHeight }) => {
              const containerWidth = getParentWidth(ref);
              const containerHeight = windowHeight - yOffset - 10; // include 10px of offset so it doesn't bleed off the page
              const imageHeight = containerWidth / imageRatio;
              const isContainerHeightLargerThanStackedImagesAtMaxCrop =
                imageHeight * 2 * MAX_CROP_AMOUNT < containerHeight;

              const containerStyles = {
                display: 'flex',
                flexDirection: 'column',
                width: containerWidth,
                maxHeight: containerHeight
              };
              const imageStyles = { maxHeight: imageHeight };

              return (
                <BreakpointMatch breakpoint="large">
                  {isLarge => {
                    return (
                      <div
                        ref={ref}
                        id="byo-cgi-container"
                        style={isLarge ? containerStyles : {}}>
                        <Route path="/config">
                          {({ match, location, history }) =>
                            match ? (
                              <React.Fragment>
                                <Profile
                                  cgiDisclaimers={cgiDisclaimers}
                                  style={isLarge ? imageStyles : {}}
                                  images={images.exterior}
                                  showConvertible={isConvertible}
                                  isConvertibleTopDown={isConvertibleTopDown}
                                  onToggleTop={onToggleConvertibleTop}
                                />
                                {isLarge && (
                                  <AttributesLarge
                                    attributes={attributes}
                                    selections={config.selections}
                                  />
                                )}
                              </React.Fragment>
                            ) : (
                              <Carousel
                                cgiDisclaimers={cgiDisclaimers}
                                style={isLarge ? imageStyles : {}}
                                exteriorImages={images.exterior}
                                exteriorIndex={exteriorIndex}
                                interiorImages={images.interior}
                                interiorIndex={interiorIndex}
                                isStacked={
                                  isLarge &&
                                  isInteriorEnabled &&
                                  isContainerHeightLargerThanStackedImagesAtMaxCrop
                                }
                                isConvertible={isConvertible}
                                isInteriorPrimary={isInteriorPrimary}
                                isTopDown={isConvertibleTopDown}
                                isInteriorToggleVisible={isInteriorEnabled}
                                onChangeExteriorIndex={onChangeExteriorIndex}
                                onChangeInteriorIndex={onChangeInteriorIndex}
                                onToggleTop={onToggleConvertibleTop}
                                onToggleInterior={onToggleInterior}
                                onZoomExterior={index => {
                                  history.push(
                                    `${
                                      location.pathname
                                    }/zoom/exterior/${index}`
                                  );
                                }}
                                onZoomInterior={index => {
                                  history.push(
                                    `${
                                      location.pathname
                                    }/zoom/interior/${index}`
                                  );
                                }}
                              />
                            )
                          }
                        </Route>
                      </div>
                    );
                  }}
                </BreakpointMatch>
              );
            }}
          </WindowResize>
        )}
      </LayoutContext.Consumer>
    );
  }
}

export default CgiSection;
